import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, Search, Users, Filter } from "lucide-react";
import { INR } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CLIENT_STATUSES = ["Lead", "Proposal Sent", "Negotiation", "Active", "On Hold", "Completed", "Lost", "Inactive"];

const empty = {
  company_name: "", client_name: "", contact_person: "", phone: "", whatsapp: "", email: "",
  website: "", address: "", city: "", state: "", country: "India", gst: "", industry: "",
  lead_source: "", account_manager_id: "", status: "Lead", notes: "",
};

export default function Clients() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [amFilter, setAmFilter] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const financeOk = user?.finance_access || user?.role === "super_admin" || user?.role === "admin";
  const canCreate = user?.role === "super_admin" || user?.role === "admin";
  const range = params.get("range") || "";

  const load = async () => {
    const r = await api.get("/clients", { params: { q, status, account_manager_id: amFilter } });
    let data = r.data;
    if (range === "this-month") {
      const monthStart = new Date();
      monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
      const iso = monthStart.toISOString().slice(0, 10);
      data = data.filter((c) => (c.created_at || "") >= iso);
    }
    setClients(data);
  };

  useEffect(() => { load(); }, [q, status, amFilter, range]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setOpen(false); setForm(empty); setParams({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">CRM</div>
          <h1 className="text-4xl font-black tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            {clients.length} client{clients.length !== 1 && "s"}
            {range === "this-month" && (
              <span data-testid="clients-range-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-primary/15 text-primary">
                Added this month
                <button
                  data-testid="clients-range-clear"
                  onClick={() => { const p = new URLSearchParams(params); p.delete("range"); setParams(p); }}
                  className="ml-1 hover:text-foreground"
                >×</button>
              </span>
            )}
          </p>
        </div>
        {canCreate && (
          <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParams({}); }}>
            <SheetTrigger asChild>
              <Button data-testid="new-client-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New Client</Button>
            </SheetTrigger>
            <SheetContent className="sm:max-w-xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Client</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-4">
                <FormRow label="Company Name *"><Input data-testid="client-form-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></FormRow>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></FormRow>
                  <FormRow label="Primary Contact"><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></FormRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FormRow>
                  <FormRow label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></FormRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FormRow>
                  <FormRow label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></FormRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="GST"><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></FormRow>
                  <FormRow label="Industry"><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></FormRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="Account Manager">
                    <Select value={form.account_manager_id} onValueChange={(v) => setForm({ ...form, account_manager_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>{users.filter((u) => u.role !== "member").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </FormRow>
                  <FormRow label="Status">
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </FormRow>
                </div>
                <FormRow label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></FormRow>
                <Button data-testid="client-form-submit" onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90">{saving ? "Saving..." : "Create Client"}</Button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 w-64" />
        </div>
        <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="clients-status-filter" className="w-44"><Filter size={12} className="mr-1" /><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={amFilter || "__all__"} onValueChange={(v) => setAmFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All account managers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All account managers</SelectItem>
            {users.filter((u) => u.role !== "member").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" description="Create your first client to start tracking services, projects and payments." />
      ) : (
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2.5">Client</th>
                  <th className="px-3 py-2.5">Services</th>
                  <th className="px-3 py-2.5">Manager</th>
                  {financeOk && <th className="px-3 py-2.5 text-right">Contract</th>}
                  {financeOk && <th className="px-3 py-2.5 text-right">Received</th>}
                  {financeOk && <th className="px-3 py-2.5 text-right">Outstanding</th>}
                  <th className="px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const am = users.find((u) => u.id === c.account_manager_id);
                  return (
                    <tr key={c.id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold">{c.company_name}</div>
                        <div className="text-xs text-muted-foreground">{c.city} · <span className="font-mono">{c.client_id}</span></div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(c.services || []).slice(0, 2).map((s) => <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 border border-border rounded">{s}</span>)}
                          {(c.services || []).length > 2 && <span className="text-[10px] text-muted-foreground">+{c.services.length - 2}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{am?.name || "—"}</td>
                      {financeOk && <td className="px-3 py-2.5 text-right font-mono">{INR(c.contract_value)}</td>}
                      {financeOk && <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{INR(c.received)}</td>}
                      {financeOk && <td className="px-3 py-2.5 text-right font-mono text-primary">{INR(c.outstanding)}</td>}
                      <td className="px-3 py-2.5"><StatusBadge value={c.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[10px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}
